/**
 * Centralised TanStack Query keys for the Sales resource.
 *
 * Every one of these used to key on `apiClient.sales.getX.name` — the
 * *function's* name, read at runtime. That is a build-time hazard and a
 * caching one. A minifier is free to rename `getTopProducts` to `a`, and
 * another method on the same client to `a` as well, at which point two
 * unrelated widgets share a cache entry and each shows the other's data.
 * Nothing in development reproduces it, because development is not minified.
 *
 * It is also unaddressable: `all` gives a prefix that `invalidateQueries` can
 * match, so a mutation that changes sales can refresh every sales widget in
 * one call. A key made of a function's name has no prefix anyone can name.
 *
 * The window (`from` / `to`) is part of each key: the page-level period filter
 * changes it, and the refetch follows automatically.
 */
export const SalesQueryKeys = {
    all: ['sales'] as const,
    revenueSummary: () => [...SalesQueryKeys.all, 'revenue-summary'] as const,
    hourlyActivity: () => [...SalesQueryKeys.all, 'hourly-activity'] as const,
    revenueDynamics: (from: string, to: string, productId: string | null) =>
        [
            ...SalesQueryKeys.all,
            'revenue-dynamics',
            from,
            to,
            productId,
        ] as const,
    branchComparison: (from: string, to: string) =>
        [...SalesQueryKeys.all, 'branch-comparison', from, to] as const,
    topProducts: (from: string, to: string) =>
        [...SalesQueryKeys.all, 'top-products', from, to] as const,
    topBrands: (from: string, to: string) =>
        [...SalesQueryKeys.all, 'top-brands', from, to] as const,
    topCategories: (from: string, to: string) =>
        [...SalesQueryKeys.all, 'top-categories', from, to] as const,
    transactions: (params: {
        from: string;
        to: string;
        sellerName?: string;
        transactionType?: string;
        discounted?: true;
        pageSize?: number;
    }) =>
        [
            ...SalesQueryKeys.all,
            'transactions',
            {
                from: params.from,
                to: params.to,
                sellerName: params.sellerName ?? '',
                transactionType: params.transactionType ?? '',
                discounted: params.discounted,
                pageSize: params.pageSize ?? 20,
            },
        ] as const,
};
